import React from 'react'
import { Box, Typography, LinearProgress, Stack } from '@mui/material'
import { RadiusOfOperationMiles } from 'interfaces/ISummaryReport'

interface IMileageRadiusOfOperationProps {
  data: RadiusOfOperationMiles | null
}

interface MileageRange {
  range: string
  percentage: number
  miles: number
  color: string
}

const MileageRadiusOfOperation: React.FC<IMileageRadiusOfOperationProps> = ({
  data,
}) => {
  const transformData = (): MileageRange[] => {
    if (!data?.breakdownByRadius) return []

    const sortedBreakdown = [...data.breakdownByRadius].sort((a, b) => {
      if (a.radius.thresholdMiles === null && b.radius.thresholdMiles === null)
        return 0
      if (a.radius.thresholdMiles === null) return 1
      if (b.radius.thresholdMiles === null) return -1

      return a.radius.thresholdMiles - b.radius.thresholdMiles
    })

    return sortedBreakdown.map((item, index) => {
      const prevThreshold =
        index > 0 ? sortedBreakdown[index - 1].radius.thresholdMiles : 0
      const currentThreshold = item.radius.thresholdMiles

      let rangeLabel: string
      if (currentThreshold === null) {
        rangeLabel = `${prevThreshold}+ miles`
      } else {
        rangeLabel = `${prevThreshold}-${currentThreshold} miles`
      }

      return {
        range: rangeLabel,
        percentage: Math.round(item.metrics.percentageOfTotal * 100) / 100,
        miles: Math.round(item.metrics.distanceMiles),
        color: 'primary',
      }
    })
  }

  const mileageData = transformData()

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Stack spacing={3} sx={{ width: '100%' }}>
        {mileageData.map((item, index) => (
          <Box key={index} sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                mb: 0.5,
              }}
            >
              <Typography
                id={`radius-of-operation-mileage-radius-label-range-${index}`}
                variant="h3"
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                {item.range}
              </Typography>

              <Typography
                id={`radius-of-operation-mileage-radius-label-percentage-${index}`}
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontSize: '0.875rem',
                }}
              >
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: '#374151' }}
                >
                  {item.percentage}%
                </Box>{' '}
                of total miles
              </Typography>
            </Box>

            <Box sx={{ mb: 0.5, width: '100%', minWidth: 0 }}>
              <LinearProgress
                id={`radius-of-operation-mileage-radius-progress-bar-${index}`}
                variant="determinate"
                value={item.percentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#f3f4f6',
                  width: '100%',
                  minWidth: 0,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: item.color,
                    borderRadius: 4,
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              <Typography
                id={`radius-of-operation-mileage-radius-label-miles-${index}`}
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  fontSize: '1rem',
                }}
              >
                {item.miles.toLocaleString()} miles
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

export default MileageRadiusOfOperation
